import dedent from 'ts-dedent'
import {
  ColumnDataType,
  Language,
  Provider,
  QueryAndOutputs,
  TableOptimizationColumnDataType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class PrepareForVisualization extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.prepareforvisualization',
        group: 'Spatial Operations',
        title: 'Prepare for Visualization',
        description: `
      This component adds the necessary bounds to a
      table so it can be previewed.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        }
      ]
    )
    this.supportedProviders = [Provider.DatabricksRest]
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    let outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const prepParams = this._prepareParameters(params)

    const select = dedent`SELECT /*+ REPARTITION(40) */
          ST_XMIN(${params.geo}_processed) AS __carto_xmin,
          ST_YMIN(${params.geo}_processed) AS __carto_ymin,
          ST_XMAX(${params.geo}_processed) AS __carto_xmax,
          ST_YMAX(${params.geo}_processed) AS __carto_ymax,
          * EXCEPT(${params.geo}_processed)
        FROM (
          SELECT ${connection.processInputGeogColumn(
            params.geo as string
          )} AS ${params.geo}_processed,
            ''' + select_clause_str + '''
          FROM ${connection.quoteName(prepParams.source as string)}
        )`

    const createTable = connection.getCreateTempTableQuery(
      outputTablename,
      select,
      codeType,
      {
        column: {
          // this is a hack to use all the bounds columns. The getcreateTempTableQuery
          // method only accepts a single column, so we need to pass a string with all the
          // bounds columns separated by commas.
          name: '__carto_xmin, __carto_ymin, __carto_xmax, __carto_ymax',
          type: TableOptimizationColumnDataType.NonGeo
        }
      }
    )

    const query = `
from pyspark.sql.functions import col

df = spark.sql("select * from ${connection.quoteName(
      prepParams.source as string
    )}")


# Specify the special columns
special_columns = ['__carto_xmin', '__carto_ymin', '__carto_xmax', '__carto_ymax']

# Get the special columns that are in the table
excluded_columns = [col_name for col_name in special_columns if col_name in df.columns]

rename_geom_column = ''
geom_alias = 'l_geom'
if '${params.geo}' != 'geom':
    if 'geom' in df.columns:
        # Iterate to find a valid alias
        while geom_alias in df.columns:
            geom_alias = 'l_' + geom_alias
        rename_geom_column = rename_geom_column + f', geom AS {geom_alias}'
        excluded_columns.append('geom')
    rename_geom_column = rename_geom_column + f', ${params.geo} AS geom'


excluded_columns_string = ''
if len(excluded_columns) > 0:
    excluded_columns_string = ', '.join(excluded_columns)
    excluded_columns_string = f' EXCEPT({excluded_columns_string})'

select_clause_str = f'*{excluded_columns_string}{rename_geom_column}'

${(connection as DBricksConnection).wrapSQLCodeAsPythonCall(createTable)}
`
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  language(provider: Provider): Language {
    return Language.Python
  }
}

export { PrepareForVisualization }
